import { useCallback } from 'react';
import { adminApi } from '../../api/adminApi';
import { FK_MAP, ARRAY_FK_MAP, REVERSE_FK_MAP, CONTAINER_DISPLAY_FIELDS } from './explorerSchema';

// ─── Tree Node Types ─────────────────────────────────────────────────────────

export interface TreeNode {
  id: string;             // unique key for this node
  container: string;      // CosmosDB container
  docId: string;          // document ID
  label: string;          // display text
  relation?: string;      // e.g. "via userId", "→ modules"
  children?: TreeNode[];  // lazy-loaded children
  childrenLoaded: boolean;
  expanded: boolean;
}

function makeNodeId(container: string, docId: string): string {
  return `${container}:${docId}`;
}

// ─── Tree Component ──────────────────────────────────────────────────────────

interface ExplorerTreeProps {
  roots: TreeNode[];
  selectedNodeId: string | null;
  onSelect: (container: string, docId: string) => void;
  onUpdateRoots: (roots: TreeNode[]) => void;
}

export function ExplorerTree({ roots, selectedNodeId, onSelect, onUpdateRoots }: ExplorerTreeProps) {
  return (
    <div className="exp-tree">
      {roots.map((node, i) => (
        <TreeNodeRow
          key={node.id}
          node={node}
          depth={0}
          selectedNodeId={selectedNodeId}
          onSelect={onSelect}
          onToggle={(path) => {
            const updated = [...roots];
            updated[i] = toggleAtPath(updated[i], path);
            onUpdateRoots(updated);
          }}
          onLoadChildren={(path) => loadChildren(roots, i, path, onUpdateRoots)}
          path={[]}
        />
      ))}
    </div>
  );
}

// ─── Single Tree Row ─────────────────────────────────────────────────────────

function TreeNodeRow({ node, depth, selectedNodeId, onSelect, onToggle, onLoadChildren, path }: {
  node: TreeNode;
  depth: number;
  selectedNodeId: string | null;
  onSelect: (container: string, docId: string) => void;
  onToggle: (path: number[]) => void;
  onLoadChildren: (path: number[]) => void;
  path: number[];
}) {
  const isSelected = selectedNodeId === node.id;
  const hasChildren = node.children && node.children.length > 0;
  const canExpand = !node.childrenLoaded || hasChildren;

  const handleClick = useCallback(() => {
    onSelect(node.container, node.docId);
  }, [node.container, node.docId, onSelect]);

  const handleDoubleClick = useCallback(() => {
    if (!node.childrenLoaded) {
      onLoadChildren(path);
    } else {
      onToggle(path);
    }
  }, [node.childrenLoaded, path, onLoadChildren, onToggle]);

  return (
    <>
      <div
        className={`exp-tree-row${isSelected ? ' selected' : ''}`}
        style={{ paddingLeft: 12 + depth * 16 }}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
      >
        <span className="exp-tree-arrow" onClick={(e) => { e.stopPropagation(); handleDoubleClick(); }}>
          {!canExpand ? '\u00A0\u00A0' : node.expanded ? '\u25BC' : '\u25B6'}
        </span>
        <span className="exp-tree-badge">{node.container}</span>
        <span className="exp-tree-label" title={node.docId}>
          {node.label}
        </span>
        {node.relation && <span className="exp-tree-relation">{node.relation}</span>}
      </div>
      {node.expanded && node.children?.map((child, i) => (
        <TreeNodeRow
          key={child.id}
          node={child}
          depth={depth + 1}
          selectedNodeId={selectedNodeId}
          onSelect={onSelect}
          onToggle={onToggle}
          onLoadChildren={onLoadChildren}
          path={[...path, i]}
        />
      ))}
    </>
  );
}

// ─── Tree Operations ─────────────────────────────────────────────────────────

function toggleAtPath(node: TreeNode, path: number[]): TreeNode {
  if (path.length === 0) {
    return { ...node, expanded: !node.expanded };
  }
  const [head, ...rest] = path;
  const children = [...(node.children || [])];
  children[head] = toggleAtPath(children[head], rest);
  return { ...node, children, expanded: true };
}

function updateAtPath(node: TreeNode, path: number[], updater: (n: TreeNode) => TreeNode): TreeNode {
  if (path.length === 0) return updater(node);
  const [head, ...rest] = path;
  const children = [...(node.children || [])];
  children[head] = updateAtPath(children[head], rest, updater);
  return { ...node, children };
}

async function loadChildren(roots: TreeNode[], rootIdx: number, path: number[], setRoots: (roots: TreeNode[]) => void) {
  const target = getNodeAtPath(roots[rootIdx], path);
  if (!target || target.childrenLoaded) {
    // Already loaded — just toggle
    const updated = [...roots];
    updated[rootIdx] = toggleAtPath(updated[rootIdx], path);
    setRoots(updated);
    return;
  }

  const children: TreeNode[] = [];

  // 1. Load the document to find FK references (outgoing)
  try {
    const result = await adminApi.getExplorerDocument(target.container, target.docId);
    if (result.document) {
      const fks = FK_MAP[target.container] || {};
      for (const [fieldPath, fk] of Object.entries(fks)) {
        const val = getNestedField(result.document, fieldPath);
        if (typeof val === 'string' && val.length > 0) {
          // Get a preview label
          const preview = await getPreviewLabel(fk.container, val);
          children.push({
            id: makeNodeId(fk.container, val),
            container: fk.container,
            docId: val,
            label: preview,
            relation: `\u2192 ${fk.label}`,
            childrenLoaded: false,
            expanded: false,
          });
        }
      }

      const arrayFks = ARRAY_FK_MAP[target.container] || {};
      for (const [fieldName, fk] of Object.entries(arrayFks)) {
        const arr = result.document[fieldName];
        if (Array.isArray(arr)) {
          for (const item of arr.slice(0, 20)) { // limit to 20
            if (typeof item === 'string' && item.length > 0) {
              children.push({
                id: makeNodeId(fk.container, item),
                container: fk.container,
                docId: item,
                label: item.slice(0, 16) + '...',
                relation: `\u2192 ${fk.label}`,
                childrenLoaded: false,
                expanded: false,
              });
            }
          }
        }
      }
    }
  } catch { /* ignore fetch errors */ }

  // 2. Load reverse references (incoming)
  const reverseEntries = REVERSE_FK_MAP[target.container] || [];
  for (const entry of reverseEntries) {
    try {
      const result = await adminApi.getExplorerReferences(entry.sourceContainer, entry.sourceField, target.docId, entry.isArray);
      for (const refId of result.ids.slice(0, 20)) { // limit to 20
        children.push({
          id: makeNodeId(entry.sourceContainer, refId) + ':' + entry.sourceField,
          container: entry.sourceContainer,
          docId: refId,
          label: refId.slice(0, 16) + '...',
          relation: `\u2190 ${entry.sourceContainer} via ${entry.sourceField}`,
          childrenLoaded: false,
          expanded: false,
        });
      }
    } catch { /* ignore */ }
  }

  // 3. Load user-tag references for questions and submissions
  if (target.container === 'questions' || target.container === 'submissions') {
    const itemType = target.container === 'questions' ? 'question' : 'submission';
    try {
      const result = await adminApi.getExplorerUserTagReferences(target.docId, itemType as 'question' | 'submission');
      for (const tag of result.tags.slice(0, 20)) {
        children.push({
          id: makeNodeId('user-tags', tag.id) + ':tag',
          container: 'user-tags',
          docId: tag.id,
          label: `"${tag.tag}" (${tag.userId.slice(0, 8)}...)`,
          relation: `\u2190 user-tags: ${tag.tag}`,
          childrenLoaded: false,
          expanded: false,
        });
      }
    } catch { /* ignore */ }
  }

  const updated = [...roots];
  updated[rootIdx] = updateAtPath(updated[rootIdx], path, (n) => ({
    ...n,
    children,
    childrenLoaded: true,
    expanded: true,
  }));
  setRoots(updated);
}

function getNodeAtPath(node: TreeNode, path: number[]): TreeNode | null {
  if (path.length === 0) return node;
  const [head, ...rest] = path;
  if (!node.children || !node.children[head]) return null;
  return getNodeAtPath(node.children[head], rest);
}

function getNestedField(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((o, key) => {
    if (o && typeof o === 'object') return (o as Record<string, unknown>)[key];
    return undefined;
  }, obj);
}

async function getPreviewLabel(container: string, id: string): Promise<string> {
  try {
    const fields = CONTAINER_DISPLAY_FIELDS[container];
    if (!fields || fields.length === 0) return id.slice(0, 16) + '...';
    const result = await adminApi.getExplorerDocument(container, id);
    if (!result.document) return id.slice(0, 16) + '...';
    const firstField = fields[0];
    const val = getNestedField(result.document, firstField);
    if (typeof val === 'string' && val.length > 0) {
      return val.length > 40 ? val.slice(0, 40) + '...' : val;
    }
    return id.slice(0, 16) + '...';
  } catch {
    return id.slice(0, 16) + '...';
  }
}

// ─── Helper to create a root node ────────────────────────────────────────────

export function createRootNode(container: string, docId: string, label?: string): TreeNode {
  return {
    id: makeNodeId(container, docId),
    container,
    docId,
    label: label || (docId.length > 16 ? docId.slice(0, 16) + '...' : docId),
    childrenLoaded: false,
    expanded: false,
  };
}
